import { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Avatar, Button, Dropdown, Layout, Menu, Space, Typography } from 'antd';
import type { MenuProps } from 'antd';
import {
  AppstoreOutlined,
  BellOutlined,
  BulbOutlined,
  ClusterOutlined,
  DashboardOutlined,
  EnvironmentOutlined,
  ExperimentOutlined,
  LogoutOutlined,
  MobileOutlined,
  MoonOutlined,
  QrcodeOutlined,
  SafetyOutlined,
  SettingOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useAuth } from '@/auth/useAuth';
import { useThemeStore } from '@/stores/themeStore';
import { MdmSelector } from '@/components/header/MdmSelector';
import './MainLayout.css';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const menuItems: MenuProps['items'] = [
  { key: '/', icon: <DashboardOutlined />, label: 'Dashboard' },
  { key: '/devices', icon: <MobileOutlined />, label: 'Dispositivos' },
  { key: '/fleet-map', icon: <EnvironmentOutlined />, label: 'Mapa de flota' },
  { key: '/enrollment', icon: <QrcodeOutlined />, label: 'Enrolamiento' },
  { key: '/profiles', icon: <SafetyOutlined />, label: 'Perfiles' },
  { key: '/apps', icon: <AppstoreOutlined />, label: 'Aplicaciones' },
  { key: '/commands', icon: <ThunderboltOutlined />, label: 'Comandos' },
  { key: '/compliance', icon: <SafetyOutlined />, label: 'Compliance' },
  {
    key: 'admin',
    icon: <SettingOutlined />,
    label: 'Administracion',
    children: [
      { key: '/admin/mdms', icon: <ClusterOutlined />, label: 'MDMs' },             // [3-tier] NUEVO
      { key: '/admin/enterprises', icon: <TeamOutlined />, label: 'Enterprises' },
      { key: '/admin/users', icon: <UserOutlined />, label: 'Usuarios' },
      { key: '/admin/jobs', icon: <ExperimentOutlined />, label: 'Jobs (Hangfire)' },
    ],
  },
];

export function MainLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { mode, toggle } = useThemeStore();

  const userMenu: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Mi perfil',
      onClick: () => navigate('/profile'),
    },
    { type: 'divider' },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Cerrar sesion',
      onClick: () => logout(),
    },
  ];

  return (
    <Layout className="main-layout">
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        width={232}
        className="main-layout__sider"
      >
        <div className="main-layout__logo">
          {collapsed ? 'M' : 'MDM Platform'}
        </div>
        <Menu
          theme="light"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={({ key }) => navigate(key)}
        />
      </Sider>
      <Layout>
        <Header className="main-layout__header">
          {/* [3-tier] Selector de MDM activo a la izquierda */}
          <div style={{ flex: 1 }}>
            <MdmSelector />
          </div>

          <div className="main-layout__header-right">
            <Space size="middle">
              <Button
                type="text"
                icon={mode === 'light' ? <MoonOutlined /> : <BulbOutlined />}
                onClick={toggle}
                title={mode === 'light' ? 'Modo oscuro' : 'Modo claro'}
              />
              <Button type="text" icon={<BellOutlined />} title="Notificaciones" />
              <Dropdown menu={{ items: userMenu }} placement="bottomRight">
                <Space className="main-layout__user">
                  <Avatar style={{ backgroundColor: 'var(--color-primary)' }} icon={<UserOutlined />} />
                  <div className="main-layout__user-info">
                    <Text strong>{user?.fullName || user?.email}</Text>
                    {user?.currentEnterpriseName && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {user.currentEnterpriseName}
                      </Text>
                    )}
                  </div>
                </Space>
              </Dropdown>
            </Space>
          </div>
        </Header>
        <Content className="main-layout__content">
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
